/**
 * 위임 담당자 축 진행 문구(S39-c) — 목록 상태 배지 아래 「박담당 작성 중」.
 *
 * ## 왜 `lib/reports`가 아니라 별도 모듈인가
 * 이 술어는 목록 배지 컴포넌트가 import하는데, `lib/reports`를 `vi.mock`하는 spec이 여럿이라
 * 거기에 새 export를 넣으면 그 spec들이 전부 `No "X" export is defined on the mock`으로 죽는다
 * (vitest 팩토리 mock은 부분 mock이 아니다 — `memory/feedback-pure-predicate-must-not-live-in-a-vi-mocked-module`).
 * 순수 함수라 딸린 것도 없으므로 `lib/memberStatus` 옆에 둔다.
 *
 * ## 계약
 * 판정(어느 단계인가)은 **서버가 한다** — shared `deriveDelegationProgress` 하나가 소유하고
 * 응답의 `delegationProgress`로 내려온다. 여기서는 라벨만 붙인다(`report.status`와 같은 방식).
 * 단계 문구도 shared `getDelegationProgressLabel`가 정본이라 상세와 갈라지지 않는다.
 *
 * ## 상태 게이트 — 두 축이 한 문서에 동시에 붙지 않는다
 * 제출 뒤(`PENDING_APPROVAL`~)는 결재자 축(`resolveApprovalProgressLabel`)이 이어받는다
 * (S39 축 확정, 2026-08-30). 서버 목록도 작성 단계에만 진행을 싣지만, 화면에서도 같은 게이트를
 * 둔다 — 응답 계약이 넓어져도 목록에 라벨 두 줄이 겹치지 않게 하기 위해서다.
 */
import { getDelegationProgressLabel, type DelegationProgress } from '@safetyhub/shared';

/**
 * 담당자가 아직 작성 중인 단계 — `REJECTED`는 반려 후 재작성 구간이다.
 *
 * §7(2026-09-06)로 **신규 위임은 `CREATING`으로 태어난다.** 그 구간이야말로 "발송됨 → 열람 →
 * 작성 중"이 필요한 자리인데(아직 아무 내용도 없는 문서라 상태 라벨 '생성 중'만으로는 담당자가
 * 링크를 열었는지조차 알 수 없다), 여기 게이트가 옛 두 값에 머물러 있으면 **서버가 실어 보낸
 * 진행값을 화면이 조용히 버린다** — 서버 목록 파생은 이미 CREATING을 포함한다
 * (`reports.service.findAll`의 `delegationProgressByReportId` 필터).
 * 기존 위임 문서(DRAFT 탄생, grandfather)는 DRAFT 축이 그대로 받는다.
 */
const DELEGATION_PROGRESS_STATUSES = new Set(['CREATING', 'DRAFT', 'REJECTED']);

export interface DelegationProgressLabelInput {
  status: string;
  delegatedContact?: { name?: string | null } | null;
  delegationProgress?: DelegationProgress | null;
}

/**
 * 「담당자 이름 + 진행 단계」 — 붙일 게 없으면 null(위임이 아니거나·해제됐거나·제출 뒤).
 * 이름이 비어 있으면 단계만 보여 준다(빈칸 + 단계보다 낫다).
 */
export function resolveDelegationProgressLabel(
  report: DelegationProgressLabelInput,
): string | null {
  if (!DELEGATION_PROGRESS_STATUSES.has(report.status)) return null;
  const progress = report.delegationProgress;
  if (!progress) return null;
  const stage = getDelegationProgressLabel(progress.state);
  const name = report.delegatedContact?.name?.trim();
  return name ? `${name} ${stage}` : stage;
}
